import { useCallback, useState } from 'react';
import { API_BASE_URL } from '../../config.js';

/**
 * One request helper for every Picture AI screen.
 *
 * The backend answers `{ error }` with a status that already carries meaning, so the message is
 * surfaced as-is rather than replaced by a generic one.
 */
export const usePictureAiApi = (fetchWithAuth) => useCallback(async (path, options = {}) => {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/picture-ai${path}`, {
    ...options,
    headers: options.body ? { 'Content-Type': 'application/json', ...(options.headers || {}) } : options.headers
  });
  if (response.status === 204) return null;
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || `Erreur ${response.status}`);
  return payload;
}, [fetchWithAuth]);

/** Busy flag plus the last message, the pattern every screen here repeats. */
export const useBusyMessage = () => {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const run = useCallback(async (operation, successMessage) => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await operation();
      if (successMessage) setMessage({ tone: 'success', text: successMessage(result) });
      return result;
    } catch (error) {
      setMessage({ tone: 'error', text: error.message });
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  return { busy, message, setMessage, run };
};

export const inputClass = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white';
export const buttonClass = 'rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50';
export const ghostButtonClass = 'rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800';
export const cardClass = 'rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900';

export const formatCost = (micros) => {
  if (micros === null || micros === undefined) return '—';
  return `${(Number(micros) / 1_000_000).toFixed(2)} $`;
};
